/* CarbonERA review carousel: 3 reviews on desktop, 2 tablet, 1 mobile.
 * One-position advance per 5s; wraps over the 10 actual Judge.me reviews.
 * No duplicate reviews, no cards, no new review source. */
(() => {
  'use strict';
  const instances = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  function initialize(root) {
    if (instances.has(root)) return;
    const slides = Array.from(root.querySelectorAll('[data-ce-review-slide]'));
    const previous = root.querySelector('[data-ce-review-prev]');
    const next = root.querySelector('[data-ce-review-next]');
    const pause = root.querySelector('[data-ce-review-pause]');
    const status = root.querySelector('[data-ce-review-status]');
    if (!slides.length || !previous || !next || !pause) return;
    const controller = new AbortController();
    const { signal } = controller;
    let index = 0, timer = null, observer = null, inView = !('IntersectionObserver' in window);
    let userPaused = reducedMotion.matches, hovered = false, focused = false, editing = false, destroyed = false;
    let screenWidth = window.innerWidth;
    const delay = 5000;
    const visibleCount = () => Math.min(slides.length, window.innerWidth >= 1100 ? 3 : window.innerWidth >= 700 ? 2 : 1);
    const playable = () => !destroyed && !userPaused && !hovered && !focused &&
      !editing && inView && !document.hidden && slides.length > visibleCount();
    function clear() { if (timer !== null) clearTimeout(timer); timer = null; }
    function renderPause() {
      pause.textContent = userPaused ? 'Play' : 'Pause';
      pause.setAttribute('aria-label', userPaused ? 'Resume automatic review rotation' : 'Pause automatic review rotation');
      pause.setAttribute('aria-pressed', String(userPaused));
    }
    function render(announce = false) {
      const num = visibleCount();
      slides.forEach((slide, i) => {
        const visible = ((i - index + slides.length) % slides.length) < num;
        // Keep cycling reviews in visual order, e.g. 09, 10, 01.
        slide.style.order = String((i - index + slides.length) % slides.length);
        slide.hidden = !visible;
        slide.setAttribute('aria-hidden', String(!visible));
      });
      const first = String(index + 1).padStart(2, '0');
      const last = String(((index + num - 1) % slides.length) + 1).padStart(2, '0');
      if (status) status.textContent = announce ? 'Showing reviews ' + first + ' through ' + last + ' of ' + slides.length : '';
    }
    function schedule() {
      clear();
      if (playable()) timer = window.setTimeout(() => change(1, false), delay);
    }
    function change(offset, announce) {
      index = (index + offset + slides.length) % slides.length;
      render(announce);
      schedule();
    }
    previous.addEventListener('click', () => change(-1, true), { signal });
    next.addEventListener('click', () => change(1, true), { signal });
    pause.addEventListener('click', () => {
      userPaused = !userPaused;
      renderPause();
      schedule();
    }, { signal });
    root.addEventListener('mouseenter', () => { hovered = true; clear(); }, { signal });
    root.addEventListener('mouseleave', () => { hovered = false; schedule(); }, { signal });
    root.addEventListener('focusin', () => { focused = true; clear(); }, { signal });
    root.addEventListener('focusout', () => {
      window.requestAnimationFrame(() => {
        focused = root.contains(document.activeElement);
        schedule();
      });
    }, { signal });
    window.addEventListener('resize', () => {
      if (window.innerWidth !== screenWidth) {
        screenWidth = window.innerWidth;
        render();
        schedule();
      }
    }, { passive: true, signal });
    document.addEventListener('visibilitychange', schedule, { signal });
    reducedMotion.addEventListener('change', () => {
      if (reducedMotion.matches) userPaused = true;
      renderPause();
      schedule();
    }, { signal });
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        inView = Boolean(entries[0]?.isIntersecting);
        schedule();
      }, { threshold: 0.15 });
      observer.observe(root);
    }
    render();
    renderPause();
    const solo = slides.length <= visibleCount();
    if (solo) previous.disabled = next.disabled = pause.disabled = true;
    instances.set(root, {
      destroy() {
        destroyed = true;
        clear();
        observer?.disconnect();
        controller.abort();
        instances.delete(root);
      },
      selection(value) { editing = value; schedule(); }
    });
    schedule();
  }
  function sections(scope) {
    const found = [];
    if (scope?.matches?.('[data-ce-review-rotator]')) found.push(scope);
    scope?.querySelectorAll?.('[data-ce-review-rotator]').forEach(x => found.push(x));
    return found;
  }
  const start = () => sections(document).forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', e => sections(e.target).forEach(initialize));
  document.addEventListener('shopify:section:unload', e => sections(e.target).forEach(x => instances.get(x)?.destroy()));
  document.addEventListener('shopify:section:select', e => sections(e.target).forEach(x => instances.get(x)?.selection(true)));
  document.addEventListener('shopify:section:deselect', e => sections(e.target).forEach(x => instances.get(x)?.selection(false)));
})();
